import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaQrcode, FaDownload, FaCopy, FaArrowLeft, FaPrint } from 'react-icons/fa';
import { enquiryAPI } from '../../services/api';

const QRCodeGenerator = () => {
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);

  const generateQRCode = async () => {
    try {
      setGenerating(true);
      const response = await enquiryAPI.generateQRCode();
      setQrCodeData(response.data);
    } catch (error) {
      console.error('Error generating QR code:', error);
    } finally {
      setGenerating(false);
    }
  };

  const downloadQRCode = () => {
    if (!qrCodeData) return;
    
    const link = document.createElement('a');
    link.href = qrCodeData.qr_code_url;
    link.download = `enquiry-qr-${qrCodeData.qr_code_id}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyFormUrl = () => {
    if (!qrCodeData) return;
    
    navigator.clipboard.writeText(qrCodeData.form_url).catch(() => {
      console.error('Failed to copy URL');
    });
  };

  const printQRCode = () => {
    if (!qrCodeData) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Enquiry QR Code</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              text-align: center; 
              padding: 20px; 
              margin: 0; 
            }
            .qr-container { 
              max-width: 400px; 
              margin: 0 auto; 
              padding: 20px; 
              border: 2px solid #333; 
              border-radius: 10px; 
            }
            .qr-code { 
              max-width: 300px; 
              height: auto; 
              margin: 20px auto; 
            }
            .title { 
              font-size: 24px; 
              font-weight: bold; 
              margin-bottom: 10px; 
              color: #333; 
            }
            .subtitle { 
              font-size: 16px; 
              color: #666; 
              margin-bottom: 20px; 
            }
            .instructions { 
              font-size: 14px; 
              color: #888; 
              margin-top: 20px; 
            }
            .qr-id { 
              font-size: 12px; 
              color: #999; 
              margin-top: 10px; 
            }
            @media print {
              body { margin: 0; }
              .qr-container { border: none; }
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <div class="title">Neurons School of Innovation and AI</div>
            <div class="subtitle">Course Enquiry Form</div>
            <img src="${qrCodeData.qr_code_url}" alt="QR Code" class="qr-code" />
            <div class="instructions">
              Scan this QR code with your phone camera<br>
              to access our course enquiry form
            </div>
            <div class="qr-id">QR ID: ${qrCodeData.qr_code_id}</div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="container mx-auto px-4 py-8 bg-gray-900 min-h-screen">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/enquiries')}
            className="bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-lg transition-colors shadow-lg"
          >
            <FaArrowLeft />
          </button>
          <h1 className="text-3xl font-bold text-white">Generate QR Code</h1>
        </div>

        <div className="bg-gray-800 rounded-lg shadow-lg p-8 border border-gray-700">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaQrcode className="text-3xl text-blue-400" />
            </div>
            <h2 className="text-2xl font-semibold text-white mb-2">
              Course Enquiry QR Code
            </h2>
            <p className="text-gray-300">
              Generate a QR code that visitors can scan to access the enquiry form on their device
            </p>
          </div>

          {!qrCodeData ? (
            <div className="text-center">
              <button
                onClick={generateQRCode}
                disabled={generating}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-8 py-3 rounded-lg flex items-center gap-3 mx-auto transition-colors"
              >
                <FaQrcode />
                {generating ? 'Generating...' : 'Generate QR Code'}
              </button>
              <p className="text-sm text-gray-400 mt-4">
                QR codes expire after 24 hours for security
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* QR Code Display */}
              <div className="text-center">
                <div className="bg-gray-700 rounded-lg p-6 inline-block border border-gray-600">
                  <img
                    src={qrCodeData.qr_code_url}
                    alt="QR Code"
                    className="w-64 h-64 mx-auto"
                  />
                </div>
                <div className="mt-4">
                  <p className="text-sm text-gray-300 mb-2">QR Code ID: {qrCodeData.qr_code_id}</p>
                  <p className="text-xs text-gray-400">
                    Expires in 24 hours • Form URL: {qrCodeData.form_url}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={downloadQRCode}
                  className="bg-green-600 hover:bg-green-700 text-white p-3 rounded-lg flex flex-col items-center gap-2 transition-colors"
                >
                  <FaDownload />
                  <span className="text-sm">Download</span>
                </button>
                
                <button
                  onClick={copyFormUrl}
                  className="bg-purple-600 hover:bg-purple-700 text-white p-3 rounded-lg flex flex-col items-center gap-2 transition-colors"
                >
                  <FaCopy />
                  <span className="text-sm">Copy URL</span>
                </button>
                
                <button
                  onClick={printQRCode}
                  className="bg-orange-600 hover:bg-orange-700 text-white p-3 rounded-lg flex flex-col items-center gap-2 transition-colors"
                >
                  <FaPrint />
                  <span className="text-sm">Print</span>
                </button>
              </div>

              {/* Instructions */}
              <div className="bg-blue-900 border border-blue-700 rounded-lg p-4">
                <h3 className="font-semibold text-blue-200 mb-2">How to use:</h3>
                <ul className="text-sm text-blue-100 space-y-1">
                  <li>• Display this QR code at your reception desk or entrance</li>
                  <li>• Visitors can scan it with their phone camera</li>
                  <li>• They'll be taken to a mobile-friendly enquiry form</li>
                  <li>• Form submissions will be automatically recorded</li>
                  <li>• Thank you emails with brochures will be sent automatically</li>
                </ul>
              </div>

              {/* Generate New Button */}
              <div className="text-center pt-4 border-t border-gray-600">
                <button
                  onClick={generateQRCode}
                  disabled={generating}
                  className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg flex items-center gap-2 mx-auto transition-colors"
                >
                  <FaQrcode />
                  Generate New QR Code
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRCodeGenerator; 